import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useContext } from 'react';
import { AuthContext } from './AuthContext';
import { setCurrentTenantId } from '../lib/api';
import type { TenantMembership } from '../lib/auth';

interface WorkspaceContextValue {
  currentWorkspace: TenantMembership | null;
  workspaces: TenantMembership[];
  switchWorkspace: (tenantId: string) => void;
  isWorkspaceReady: boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextValue>({
  currentWorkspace: null,
  workspaces: [],
  switchWorkspace: () => {},
  isWorkspaceReady: false,
});

const WORKSPACE_STORAGE_KEY = 'sg-current-workspace';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [currentWorkspace, setCurrentWorkspace] =
    useState<TenantMembership | null>(null);

  const workspaces = user?.tenantMemberships || [];

  // Restore last workspace from localStorage or pick first
  useEffect(() => {
    if (!isAuthenticated || workspaces.length === 0) {
      setCurrentWorkspace(null);
      setCurrentTenantId(null);
      return;
    }

    const savedId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    const saved = workspaces.find((w) => w.tenantId === savedId);
    const workspace = saved || workspaces[0];

    setCurrentWorkspace(workspace);
    setCurrentTenantId(workspace.tenantId);
  }, [isAuthenticated, workspaces]);

  const switchWorkspace = useCallback(
    (tenantId: string) => {
      const workspace = workspaces.find((w) => w.tenantId === tenantId);
      if (!workspace) return;

      setCurrentWorkspace(workspace);
      setCurrentTenantId(workspace.tenantId);
      localStorage.setItem(WORKSPACE_STORAGE_KEY, tenantId);
    },
    [workspaces]
  );

  const value = useMemo(
    () => ({
      currentWorkspace,
      workspaces,
      switchWorkspace,
      isWorkspaceReady: currentWorkspace !== null,
    }),
    [currentWorkspace, workspaces, switchWorkspace]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
